import { computed, reactive, vueSet, vueDelete } from '@mimir/web/compat/vueCompat';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import { normalizeFieldsBeforeSave, SchemaViewSanitizer } from '@mimir/common/mdf/metadataSchema';
import { doesSchemaSupportView } from '@mimir/common/mdf/MetaDataForm';
import { DEFAULT_MDF_ID } from '@mimir/common/mdf/mdfDefaults';
import { TIMED_METADATA_FLAVOR } from '@mimir/common/dto/timedMetadataModels';
import type { MdfCreationRequest } from '@mimir/common/dto/mdfModels';
import { ViewType } from '@mimir/common/dto/mdfModels';
import { TaskTracking } from '@mimir/common/async/taskTracking';
import { axiosErrorMessage } from '@mimir/common/util/httpError';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import type { MdfState, MdfStateDeps, MdfStateFields, MdfStateOps } from '@mimir/web/model/mdfState';
import { createMdf, getMdf, deleteMdf, putMdf } from '@mimir/web/util/api';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import { showToast, TOAST } from '@mimir/web/eventBus';

/*
  The service exposes `{ state: Readonly<MdfStateFields>, ops }`. The
  source-of-truth fields and the derived computed()s live in ONE reactive(); the
  computeds auto-unwrap to plain values, so consumers read `state.x` with no
  `.value` and no markRaw is needed (runtime-proof:
  globals/__tests__/mdfStateRuntime.spec.ts).

  Per the wiki "State & Reactivity Conventions" the state file lives here in
  globals/ and the interfaces in model/mdfState.ts. The factory stays
  browser-free: every API call and toast enters through MdfStateDeps.
*/

/**
 * Source-of-truth fields the factory mutates, on top of the public read surface.
 * `rawMdfs` and `mdfsFieldsWithValidationErrors` are internal only — consumers
 * read them through the `mdfs` / `timedMdfs` / `mdfCanBeSaved` derived fields.
 * The reactive() is annotated with this so its computed()s can reference sibling
 * fields without a circular self-inference.
 */
interface MdfStateFieldsPrivate extends MdfStateFields {
  /** Raw, unsorted schemas as loaded — the source of truth behind `mdfs`. */
  rawMdfs: MetaDataSchema[];
  mdfsFieldsWithValidationErrors: Record<string, boolean>;
}

// Views whose per-field default values participate in the "unsaved changes" check.
const DEFAULT_VALUE_VIEWS = [ViewType.ITEM, ViewType.UPLOAD, ViewType.SHARE, ViewType.SCHEDULER, ViewType.COPY_ITEM, ViewType.NEW_VERSION, ViewType.CREATE_PLACEHOLDER];

/** Whether any field's default value on the edited schema differs from the saved original. */
function fieldDefaultValuesChanged(selectedMdf: MetaDataSchema, originalMdf: MetaDataSchema): boolean {
  for (const field of selectedMdf.fields) {
    const originalField = originalMdf.fields.find(entry => entry.id === field.id);
    if (originalField && !isEqual(originalField.defaultValue, field.defaultValue)) {
      return true;
    }
  }
  return false;
}

/** Whether any view-layout default value on the edited schema differs from the saved original. */
function viewDefaultValuesChanged(selectedMdf: MetaDataSchema, originalMdf: MetaDataSchema): boolean {
  for (const view of DEFAULT_VALUE_VIEWS) {
    for (const selectedSetting of selectedMdf.views[view]) {
      const originalSetting = originalMdf.views[view]?.find(setting => selectedSetting.fieldId === setting.fieldId);
      if (!isEqual(originalSetting?.defaultValue, selectedSetting.defaultValue)) {
        return true;
      }
    }
  }
  return false;
}

/** Whether the edited schema's field- or view-level default values diverge from the loaded original. */
function hasSelectedMdfDefaultValueChanges(selectedMdf: MetaDataSchema, allMdfs: MetaDataSchema[]): boolean {
  const originalMdf = allMdfs.find(mdf => mdf.id === selectedMdf.id);
  if (!originalMdf) {
    return false;
  }
  return fieldDefaultValuesChanged(selectedMdf, originalMdf) || viewDefaultValuesChanged(selectedMdf, originalMdf);
}

/**
 * The server's own explanation for a rejected MDF request, when it sent one.
 * An empty message is treated as no explanation, so callers fall back to their
 * own wording rather than reporting a blank reason.
 */
function serverErrorMessage(error: unknown): string | undefined {
  const message = axiosErrorMessage(error);
  return message !== undefined && message.length > 0 ? message : undefined;
}

export function createMdfState(deps: MdfStateDeps): MdfState {
  // Per-instance piggyback tracker (no module-level mutable state).
  const getMdfPiggyBack = new TaskTracking<MetaDataSchema>();

  /*
    Source-of-truth fields + derived computed()s in one reactive(). Vue unwraps
    the ComputedRefs at the type and value level, so `internal.mdfs` is a plain
    MetaDataSchema[] and the private source fields (`rawMdfs`,
    `mdfsFieldsWithValidationErrors`) stay mutable for the setters below.
  */
  const internal: MdfStateFieldsPrivate = reactive({
    // Private source of truth.
    rawMdfs: [] as MetaDataSchema[],
    mdfsFieldsWithValidationErrors: {} as Record<string, boolean>,
    // Source of truth that is also part of the public read surface.
    defaultMdf: null as MetaDataSchema | null,
    selectedMdf: null as MetaDataSchema | null,
    mdfIsSaving: false,
    sanitizeMetadataEditorLevel: 0,
    // Derived.
    mdfs: computed(() => [
      ...internal.rawMdfs.filter(item => item.id === DEFAULT_MDF_ID),
      ...sortBy(internal.rawMdfs.filter(item => item.id !== DEFAULT_MDF_ID), [item => item.label.toLowerCase()]),
    ]),
    mdfCanBeSaved: computed(() => !Object.values(internal.mdfsFieldsWithValidationErrors).includes(false)),
    getMdfById: computed(() => (id: string | null | undefined) => internal.rawMdfs.find(mdf => mdf.id === id)),
    getMdfByIdOrLabel: computed(() => (idOrLabel: string) => internal.rawMdfs.find(mdf => mdf.id === idOrLabel || mdf.label === idOrLabel)),
    timedMdfs: computed(() => sortBy(internal.rawMdfs.filter(mdf => mdf.flavor === TIMED_METADATA_FLAVOR), [item => item.label.toLowerCase()])),
    hasTimedMdfs: computed(() => internal.rawMdfs.some(mdf => mdf.flavor === TIMED_METADATA_FLAVOR)),
    userMadeChangesToSelectedMdf: computed(() => {
      const { selectedMdf } = internal;
      if (!selectedMdf) {
        return false;
      }
      const originalMdf = internal.rawMdfs.find(mdf => mdf.id === selectedMdf.id);
      if (!originalMdf) {
        return false;
      }
      return !isEqual(selectedMdf, originalMdf);
    }),
    selectedMdfDefaultFieldValueChanged: computed(() => {
      const { selectedMdf } = internal;
      if (!selectedMdf) {
        return false;
      }
      return hasSelectedMdfDefaultValueChanges(selectedMdf, internal.rawMdfs);
    }),
  });

  /*
    Internal writers with no external consumers — the loaded schema list is only
    ever updated through the orchestration ops below.
  */
  function updateLoadedMdf(payload: MetaDataSchema): void {
    const existingIdx = internal.rawMdfs.findIndex(s => s.id === payload.id);
    if (existingIdx >= 0) {
      // vueSet because Vue 2 cannot track a plain assignment to an array index.
      vueSet(internal.rawMdfs, existingIdx, payload);
    } else {
      internal.rawMdfs.push(payload);
    }
    if (payload.id === DEFAULT_MDF_ID) {
      internal.defaultMdf = payload;
    }
  }
  function removeLoadedMdf(id: string): void {
    const index = internal.rawMdfs.findIndex(mdf => mdf.id === id);
    if (index >= 0) {
      internal.rawMdfs.splice(index, 1);
    }
  }

  const ops: MdfStateOps = {
    setSelectedMdf(payload: MetaDataSchema | null) {
      internal.selectedMdf = payload;
    },
    /*
      vueSet/vueDelete because these add/remove KEYS on a reactive object, which
      Vue 2 cannot track through plain assignment; properties declared in the
      reactive() literal (like the map itself) take plain assignment.
    */
    removeFieldIdValidatedState(payload: string) {
      vueDelete(internal.mdfsFieldsWithValidationErrors, payload);
    },
    setFieldIdValidatedState(payload: { id: string, valid: boolean }) {
      vueSet(internal.mdfsFieldsWithValidationErrors, payload.id, payload.valid);
    },
    clearMdfValidationState() {
      internal.mdfsFieldsWithValidationErrors = {};
    },
    shouldSanitizeMetadataEditor() {
      internal.sanitizeMetadataEditorLevel++;
    },

    setMdfs(payload: MetaDataSchema[]) {
      internal.rawMdfs = payload;
      internal.defaultMdf = payload.find(schema => schema.id === DEFAULT_MDF_ID) ?? null;
    },
    addMdf(request: MdfCreationRequest) {
      return deps.createMdf(request).then(form => {
        internal.rawMdfs.push(form);
        ops.setSelectedMdf(cloneDeep(form));
      }).catch(err => deps.onError('An error occurred while attempting to create MDF schema', err));
    },
    getOrLoadMdf(formIdOrLabel: string) {
      // To invalidate cache, call loadMdf directly instead.
      const cachedMdf = internal.getMdfByIdOrLabel(formIdOrLabel);
      if (cachedMdf && cachedMdf.loaded) {
        return Promise.resolve(cachedMdf);
      }
      return ops.loadMdf(formIdOrLabel);
    },
    loadMdf(formIdOrLabel: string) {
      return getMdfPiggyBack.runOrPiggyback(formIdOrLabel, async () => {
        const form = await deps.getMdf(formIdOrLabel);
        updateLoadedMdf(form);
        return form;
      });
    },
    removeMdf(schemaId: string) {
      return deps.deleteMdf(schemaId).then(response => {
        removeLoadedMdf(schemaId);
        return response;
      });
    },
    resetSelectedMdf() {
      const { selectedMdf } = internal;
      if (!selectedMdf) {
        return;
      }
      const originalMdf = internal.rawMdfs.find(mdf => mdf.id === selectedMdf.id);
      ops.setSelectedMdf(cloneDeep(originalMdf) ?? null);
      ops.clearMdfValidationState();
    },
    async loadAndSelectMdf(payload: { formIdOrLabel: string, sanitizeViews: boolean }) {
      const loadedMdf = await ops.loadMdf(payload.formIdOrLabel);
      if (payload.sanitizeViews) {
        const defaultMdf = internal.defaultMdf;
        const viewSanitizer = SchemaViewSanitizer.getSchemaViewSanitizer(loadedMdf, defaultMdf);
        for (const view of Object.values(ViewType)) {
          if (!doesSchemaSupportView(loadedMdf.id, loadedMdf.flavor, view, loadedMdf.isSubtype)) {
            continue;
          }
          /*
            The empty seed looks redundant against the assignment two lines
            down, but it is not: getSanitizedLayoutSettingsForView reads
            `schema.views[view]` on the very schema passed to the sanitizer, and
            dereferences it unguarded when deciding whether a custom view
            already carries default-schema settings. Without the seed a schema
            that supports the view but has no stored layout for it throws.
          */
          if (!loadedMdf.views[view]) {
            vueSet(loadedMdf.views, view, []);
          }
          const sanitizedView = viewSanitizer.getSanitizedLayoutSettingsForView(view);
          vueSet(loadedMdf.views, view, sanitizedView);
        }
      }
      ops.setSelectedMdf(cloneDeep(loadedMdf));
    },
    saveSelectedMdf() {
      const { selectedMdf } = internal;
      if (!selectedMdf) {
        return Promise.resolve();
      }
      internal.mdfIsSaving = true;

      const copyOfMDF = cloneDeep(selectedMdf);
      normalizeFieldsBeforeSave(copyOfMDF);
      return deps.putMdf(copyOfMDF).then(savedForm => {
        updateLoadedMdf(savedForm);
        ops.setSelectedMdf(cloneDeep(savedForm));
        deps.onSuccess('Saved schema successfully');
      }).catch(err => {
        deps.onError(serverErrorMessage(err) ?? `Saving schema "${copyOfMDF.label}" failed and the server reported no reason`, err);
      }).finally(() => { internal.mdfIsSaving = false; });
    },
  };

  return { state: internal, ops };
}

/*
  Application-level singleton wiring the browser-facing deps into the
  browser-free factory. Consumers read `mdfState.state.*` and call
  `mdfState.ops.*`.
*/
export const mdfState: MdfState = createMdfState({
  createMdf,
  getMdf,
  deleteMdf,
  putMdf,
  onError: logErrorAndShowToast,
  onSuccess: message => showToast(message, TOAST.SUCCESS),
});
